import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  apiClient,
  ApiError,
  localStorageTokenStore,
  setForbiddenHandler,
  setUnauthorizedHandler,
} from './api-client';
import { config } from './config';

/**
 * Auth context — estende F1-025 com suporte a:
 *   - memberships (F1-019 multi-org)
 *   - active_tenant_id + level + role (F1-019)
 *   - mfaState (F1-018 regime dual: immediate | grace_period | disabled)
 *   - switchTenant() que consome POST /auth/switch-tenant
 *   - hydrateMe() que consome GET /me apos login / refresh / switch
 *
 * Regras:
 *   - state em React pura (sem libs externas) — ADR-009
 *   - "last active tenant" por usuario persistido em localStorage
 *   - 403 active_tenant_id_mismatch dispara handler global de "lostAccess"
 */

interface LoginResponse {
  readonly access_token: string;
  readonly refresh_token: string;
  readonly expires_in: number;
}

interface SignupResponse {
  readonly user_id: string;
  readonly email: string;
  readonly email_confirmation_sent: boolean;
}

export type TenantLevel = 'root' | 'intermediate' | 'leaf';
export type Role = 'owner' | 'admin' | 'editor' | 'member' | 'viewer' | 'api_consumer';

export interface Membership {
  readonly tenant_id: string;
  readonly tenant_name?: string;
  readonly tenant_level: TenantLevel;
  readonly role: Role;
}

export type MfaRegimeKind = 'immediate' | 'grace_period' | 'disabled';

export interface MfaState {
  readonly enabled: boolean;
  readonly regime: MfaRegimeKind;
  readonly graceUntil: string | null;
}

export interface MeResponse {
  readonly user_id: string;
  readonly email: string;
  readonly name?: string | null;
  readonly active_tenant_id: string | null;
  readonly active_role: Role | null;
  readonly active_tenant_level: TenantLevel | null;
  readonly mfa_verified: boolean;
  readonly memberships: readonly Membership[];
  readonly mfa_regime?: MfaRegimeKind;
  readonly mfa_grace_until?: string | null;
  /** D-051 — campo exposto pelo backend; ausente = comportamento atual (nao redireciona). */
  readonly onboarding_completed?: boolean | null;
}

export interface SwitchTenantResponse {
  readonly access_token?: string;
  readonly refresh_token?: string;
  readonly next_active_tenant_id: string;
  readonly next_role: Role;
  readonly token_refresh_required?: boolean;
}

export interface AuthState {
  readonly isAuthenticated: boolean;
  readonly email: string | null;
  readonly name: string | null;
  readonly userId: string | null;
  readonly memberships: readonly Membership[];
  readonly activeTenantId: string | null;
  readonly activeRole: Role | null;
  readonly activeTenantLevel: TenantLevel | null;
  readonly mfaState: MfaState;
  readonly forbiddenFlash: string | null;
  readonly lostTenantFlash: boolean;
  /** D-051 — null quando campo ausente no backend (fallback gracioso). */
  readonly onboardingCompleted: boolean | null;
  readonly login: (email: string, password: string) => Promise<void>;
  readonly signup: (email: string, password: string) => Promise<SignupResponse>;
  readonly logout: () => void;
  readonly clearForbidden: () => void;
  readonly clearLostTenant: () => void;
  readonly hydrateMe: () => Promise<MeResponse | null>;
  readonly switchTenant: (tenantId: string) => Promise<void>;
  readonly setMfaVerified: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

const DEFAULT_MFA_STATE: MfaState = {
  enabled: false,
  regime: 'disabled',
  graceUntil: null,
};

function lastTenantKey(userId: string): string {
  return `mktvibe.last_tenant.${userId}`;
}

function readLastTenant(userId: string): string | null {
  try {
    return window.localStorage.getItem(lastTenantKey(userId));
  } catch {
    return null;
  }
}

function writeLastTenant(userId: string, tenantId: string): void {
  try {
    window.localStorage.setItem(lastTenantKey(userId), tenantId);
  } catch {
    // silencia
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [token, setToken] = useState<string | null>(() => localStorageTokenStore.get());
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<readonly Membership[]>([]);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [activeRole, setActiveRole] = useState<Role | null>(null);
  const [activeTenantLevel, setActiveTenantLevel] = useState<TenantLevel | null>(null);
  const [mfaState, setMfaState] = useState<MfaState>(DEFAULT_MFA_STATE);
  const [forbiddenFlash, setForbiddenFlash] = useState<string | null>(null);
  const [lostTenantFlash, setLostTenantFlash] = useState<boolean>(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);

  // Usamos ref para reagir dinamicamente no handler de 403 sem re-registrar.
  const userIdRef = useRef<string | null>(null);
  const membershipsRef = useRef<readonly Membership[]>([]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);
  useEffect(() => {
    membershipsRef.current = memberships;
  }, [memberships]);

  const resetState = useCallback((): void => {
    setUserId(null);
    setEmail(null);
    setName(null);
    setMemberships([]);
    setActiveTenantId(null);
    setActiveRole(null);
    setActiveTenantLevel(null);
    setMfaState(DEFAULT_MFA_STATE);
    setOnboardingCompleted(null);
  }, []);

  const applyMe = useCallback((me: MeResponse): void => {
    setUserId(me.user_id);
    setEmail(me.email);
    setName(me.name ?? null);
    setMemberships(me.memberships);
    setActiveTenantId(me.active_tenant_id);
    setActiveRole(me.active_role);
    setActiveTenantLevel(me.active_tenant_level);
    const regime: MfaRegimeKind = me.mfa_regime ?? 'disabled';
    setMfaState({
      enabled: me.mfa_verified,
      regime,
      graceUntil: me.mfa_grace_until ?? null,
    });
    // D-051: campo ausente no backend = null (fallback gracioso — comportamento atual).
    setOnboardingCompleted(me.onboarding_completed ?? null);
    if (me.active_tenant_id !== null) {
      writeLastTenant(me.user_id, me.active_tenant_id);
    }
  }, []);

  const hydrateMe = useCallback(async (): Promise<MeResponse | null> => {
    try {
      const me = await apiClient.get<MeResponse>('/me');
      applyMe(me);
      return me;
    } catch {
      return null;
    }
  }, [applyMe]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(null);
      resetState();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    });
    setForbiddenHandler(() => {
      setForbiddenFlash('forbidden');
    });
  }, [resetState]);

  // Hydrate /me quando token inicial existe (retomada de sessao).
  useEffect(() => {
    if (token !== null && token.length > 0 && userId === null) {
      void hydrateMe();
    }
  }, [token, userId, hydrateMe]);

  const login = useCallback(
    async (emailIn: string, password: string) => {
      const res = await apiClient.post<LoginResponse>(
        '/auth/login',
        { email: emailIn, password },
        { auth: false },
      );
      try {
        window.localStorage.setItem(config.tokenStorageKey, res.access_token);
        window.localStorage.setItem(config.refreshTokenStorageKey, res.refresh_token);
      } catch {
        // silencia
      }
      setToken(res.access_token);
      setEmail(emailIn);
      // hydrate /me para popular memberships + mfaState
      const me = await hydrateMe();
      // Se /me trouxe active_tenant_id diferente do ultimo conhecido do usuario,
      // e o ultimo ainda faz parte das memberships, tenta restaurar.
      if (me !== null) {
        const last = readLastTenant(me.user_id);
        if (
          last !== null &&
          last !== me.active_tenant_id &&
          me.memberships.some((m) => m.tenant_id === last)
        ) {
          try {
            const switchRes = await apiClient.post<SwitchTenantResponse>('/auth/switch-tenant', {
              active_tenant_id: last,
            });
            if (switchRes.access_token !== undefined) {
              window.localStorage.setItem(config.tokenStorageKey, switchRes.access_token);
              setToken(switchRes.access_token);
            }
            await hydrateMe();
          } catch {
            // Fallback silencioso — mantem active do /me.
          }
        }
      }
    },
    [hydrateMe],
  );

  const signup = useCallback(async (emailIn: string, password: string) => {
    const res = await apiClient.post<SignupResponse>(
      '/auth/signup',
      { email: emailIn, password },
      { auth: false },
    );
    return res;
  }, []);

  const logout = useCallback(() => {
    localStorageTokenStore.clear();
    setToken(null);
    resetState();
  }, [resetState]);

  const clearForbidden = useCallback(() => setForbiddenFlash(null), []);
  const clearLostTenant = useCallback(() => setLostTenantFlash(false), []);

  const switchTenant = useCallback(
    async (tenantId: string): Promise<void> => {
      try {
        const res = await apiClient.post<SwitchTenantResponse>('/auth/switch-tenant', {
          active_tenant_id: tenantId,
        });
        if (res.access_token !== undefined && res.access_token.length > 0) {
          try {
            window.localStorage.setItem(config.tokenStorageKey, res.access_token);
            if (res.refresh_token !== undefined) {
              window.localStorage.setItem(config.refreshTokenStorageKey, res.refresh_token);
            }
          } catch {
            // silencia
          }
          setToken(res.access_token);
        }
        await hydrateMe();
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          // Tenant sem membership — trata como "perdi acesso".
          const currentUserId = userIdRef.current;
          const others = membershipsRef.current.filter((m) => m.tenant_id !== tenantId);
          setLostTenantFlash(true);
          if (others.length > 0 && currentUserId !== null) {
            const target = others[0];
            if (target !== undefined) {
              try {
                await apiClient.post<SwitchTenantResponse>('/auth/switch-tenant', {
                  active_tenant_id: target.tenant_id,
                });
                await hydrateMe();
              } catch {
                // silencia — o usuario vera o flash e o switcher atualizado.
              }
            }
          }
        }
        throw err;
      }
    },
    [hydrateMe],
  );

  const setMfaVerified = useCallback((): void => {
    setMfaState((prev) => ({ ...prev, enabled: true }));
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      isAuthenticated: token !== null && token.length > 0,
      email,
      name,
      userId,
      memberships,
      activeTenantId,
      activeRole,
      activeTenantLevel,
      mfaState,
      forbiddenFlash,
      lostTenantFlash,
      onboardingCompleted,
      login,
      signup,
      logout,
      clearForbidden,
      clearLostTenant,
      hydrateMe,
      switchTenant,
      setMfaVerified,
    }),
    [
      token,
      email,
      name,
      userId,
      memberships,
      activeTenantId,
      activeRole,
      activeTenantLevel,
      mfaState,
      forbiddenFlash,
      lostTenantFlash,
      onboardingCompleted,
      login,
      signup,
      logout,
      clearForbidden,
      clearLostTenant,
      hydrateMe,
      switchTenant,
      setMfaVerified,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
